<template>
  <div v-if="section" class="p-6">
    <!-- Framework Domain: Render markdown content (single file or multi-file tabs) -->
    <div v-if="renderedMarkdown" class="markdown-content" v-html="renderedMarkdown" @click="handleInternalLink"></div>

    <!-- Policy/Process Domains: Show structured metadata (only if no markdown) -->
    <div v-else-if="!section.markdown_content && !section.tab_contents">
      <div class="mb-6">
        <h2 class="text-2xl font-semibold mb-2" style="color: var(--text-primary);">
          {{ section.title }}
        </h2>
        <p v-if="section.description" class="leading-relaxed" style="color: var(--text-secondary);">
          {{ section.description }}
        </p>
      </div>

    <!-- Control Mappings (for Policy Domain) -->
    <div v-if="section.controls && section.controls.length > 0" class="mb-6">
      <h3 class="text-lg font-semibold mb-3 flex items-center gap-2">
        <i class="pi pi-shield" style="color: var(--primary-gold);"></i>
        <span style="color: var(--text-primary);">ASHC Controls</span>
      </h3>
      <div class="flex flex-wrap gap-2">
        <Tag
          v-for="control in section.controls"
          :key="control"
          :value="control"
          severity="info"
          icon="pi pi-shield"
          class="cursor-pointer hover:bg-blue-100"
        />
      </div>
    </div>

    <!-- ISO 42001 Controls -->
    <div v-if="section.isoControls && section.isoControls.length > 0" class="mb-6">
      <h3 class="text-lg font-semibold mb-3">ISO 42001 Controls</h3>
      <div class="flex flex-wrap gap-2">
        <Tag
          v-for="isoControl in section.isoControls"
          :key="isoControl"
          :value="isoControl"
          severity="success"
          icon="pi pi-check-circle"
        />
      </div>
    </div>

    <!-- Base Practices (for Process Domain) -->
    <div v-if="section.basePractices && section.basePractices.length > 0" class="mb-6">
      <h3 class="text-lg font-semibold mb-3">Base Practices</h3>
      <div class="flex flex-wrap gap-2">
        <Tag
          v-for="bp in section.basePractices"
          :key="bp"
          :value="bp"
          severity="warning"
          icon="pi pi-sitemap"
          class="cursor-pointer hover:bg-orange-100"
        />
      </div>
    </div>

    <!-- Linked Process Domains -->
    <div v-if="section.linkedProcessDomains && section.linkedProcessDomains.length > 0" class="mb-6">
      <h3 class="text-lg font-semibold mb-3">Linked Process Domains</h3>
      <div class="flex flex-wrap gap-2">
        <Chip
          v-for="domain in section.linkedProcessDomains"
          :key="domain"
          :label="domain"
          icon="pi pi-link"
        />
      </div>
    </div>

      <!-- Empty State -->
      <div v-if="!hasContent" class="text-center py-12">
        <i class="pi pi-info-circle text-4xl mb-4" style="color: var(--text-muted);"></i>
        <p style="color: var(--text-secondary);">No overview information available for this section.</p>
      </div>
    </div>
  </div>

  <div v-else class="p-6 text-center">
    <i class="pi pi-inbox text-4xl mb-4" style="color: var(--text-muted);"></i>
    <p style="color: var(--text-secondary);">Select a section to view details</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Tag from 'primevue/tag';
import Chip from 'primevue/chip';
import type { Section } from '@/types/ashmatics';
import { useMarkdown } from '@/composables/useMarkdown';

const props = defineProps<{
  section: Section | null;
  tabName?: string; // Optional: for multi-tab content (e.g., 'overview', 'regulations', 'technology')
}>();

const { renderMarkdown, handleInternalLink: markdownHandleInternalLink } = useMarkdown();

// Render markdown content if present (Framework domain)
const renderedMarkdown = computed(() => {
  // If section has tab_contents (multi-file tabs), use the specific tab's content
  if (props.section?.tab_contents && props.tabName) {
    const tabContent = props.section.tab_contents[props.tabName];
    if (tabContent) {
      return renderMarkdown(tabContent);
    }
  }

  // Fall back to single markdown_content (legacy single-file approach)
  if (props.section?.markdown_content) {
    return renderMarkdown(props.section.markdown_content);
  }

  return '';
});

// Handle internal link clicks
const handleInternalLink = (event: MouseEvent) => {
  markdownHandleInternalLink(event);
};

const hasContent = computed(() => {
  if (!props.section) return false;
  return !!(
    props.section.markdown_content ||
    props.section.tab_contents ||
    props.section.description ||
    (props.section.controls && props.section.controls.length > 0) ||
    (props.section.isoControls && props.section.isoControls.length > 0) ||
    (props.section.basePractices && props.section.basePractices.length > 0) ||
    (props.section.linkedProcessDomains && props.section.linkedProcessDomains.length > 0)
  );
});
</script>

<style scoped>
/* Markdown content styling */
.markdown-content {
  line-height: 1.7;
  color: var(--text-primary);
}

.markdown-content :deep(h1) {
  font-size: 2rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.markdown-content :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
  border-bottom: 2px solid var(--border-light);
  padding-bottom: 0.5rem;
}

.markdown-content :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.markdown-content :deep(p) {
  margin-bottom: 1rem;
  color: var(--text-secondary);
}

.markdown-content :deep(a) {
  color: var(--primary-gold);
  text-decoration: underline;
  cursor: pointer;
}

.markdown-content :deep(a:hover) {
  color: var(--warm-brown);
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.markdown-content :deep(li) {
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
}

.markdown-content :deep(code) {
  background: var(--background-secondary);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: 'Courier New', monospace;
}

.markdown-content :deep(pre) {
  background: var(--background-secondary);
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.markdown-content :deep(pre code) {
  background: none;
  padding: 0;
}

.markdown-content :deep(blockquote) {
  border-left: 4px solid var(--primary-gold);
  padding-left: 1rem;
  margin: 1rem 0;
  color: var(--text-secondary);
  font-style: italic;
}

.markdown-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid var(--border-light);
  padding: 0.75rem;
  text-align: left;
}

.markdown-content :deep(th) {
  background: var(--background-secondary);
  font-weight: 600;
  color: var(--text-primary);
}

.markdown-content :deep(hr) {
  border: none;
  border-top: 2px solid var(--border-light);
  margin: 2rem 0;
}
</style>
